import React, { useState, useMemo, useEffect, useCallback } from "react";
//-tech-debt-react-query;
import { keepPreviousData, useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  IconButton,
  Tooltip,
  SelectChangeEvent,
  Chip,
  TablePagination,
  Skeleton,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Paper,
  OutlinedInput,
  ListItemText,
  Checkbox,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import { deleteItem, postItem, putItem, fetchItems } from "../../support/queries";
import { ModelConfig, ModelField, isSelectField } from "./RbacConfig";
import { AdminTable } from "./AdminTable";
import { AdminDialog } from "./AdminDialog";
import StbLoader from "../security-toolbox-ui/StbLoader";
import StbApiModal from "../security-toolbox-ui/StbApiModal";
import { DjangoField, DjangoModel, RelatedDialogConfig } from '../../types/modelAdmin';


// --- UTILS ---
function getRelatedDbTable(field: DjangoField): string {
  if (field.related_model) {
    return field.related_model.db_table;
  }
  const baseKey = field.key.replace(/_id$/, "");
  return baseKey;
}

function getOptionLabel(field: DjangoField): string {
  if (field.related_model?.display_field) {
    return field.related_model.display_field;
  }
  return "name";
}

function getFieldOptionLabel(field: ModelField): string {
  if (isSelectField(field)) {
    return field.optionLabel;
  }
  return "name"; // fallback
}

// Truncate text and show full text on hover
function TruncatedText({
                         text,
                         maxLength = 100,
                         showTooltip = true,
                       }: {
  text: string;
  maxLength?: number;
  showTooltip?: boolean;
}) {
  if (!text || typeof text !== "string") {
    return <span>{String(text || "")}</span>;
  }

  const isTruncated = text.length > maxLength;

  if (!isTruncated || !showTooltip) {
    return <span>{isTruncated ? `${text.substring(0, maxLength)}...` : text}</span>;
  }

  return (
    <Tooltip title={text} placement="top">
      <span style={{ cursor: "help" }}>{text.substring(0, maxLength)}...</span>
    </Tooltip>
  );
}

function renderComplexValue(value: any, maxLength = 50): React.ReactNode {
  if (value === null || value === undefined) {
    return <em style={{ color: "#999" }}>null</em>;
  }

  if (typeof value === "boolean") {
    return value ? "true" : "false";
  }

  if (typeof value === "string") {
    return <TruncatedText text={value} maxLength={100} />;
  }

  if (typeof value === "number") {
    return value.toString();
  }

  if (Array.isArray(value)) {
    if (value.length === 0) {
      return <em style={{ color: "#999" }}>[]</em>;
    }
    return (
      <Tooltip title={JSON.stringify(value, null, 2)}>
        <Chip label={`Array [${value.length}]`} size="small" variant="outlined" color="primary" />
      </Tooltip>
    );
  }

  if (typeof value === "object") {
    const keys = Object.keys(value);
    if (keys.length === 0) {
      return <em style={{ color: "#999" }}>{`{}`}</em>;
    }
    return (
      <Tooltip title={JSON.stringify(value, null, 2)}>
        <Chip label={`Object {${keys.length}}`} size="small" variant="outlined" color="secondary" />
      </Tooltip>
    );
  }

  return <TruncatedText text={String(value)} maxLength={100} />;
}

function shouldRenderAsComplex(field: DjangoField): boolean {
  const complexTypes = ["JSONField", "TextField"];
  const complexFieldNames = ["settings", "queries", "transformations", "config", "metadata", "data"];

  return (
    complexTypes.includes(field.type) ||
    complexFieldNames.some((name) => field.key.toLowerCase().includes(name.toLowerCase()))
  );
}

function djangoFieldToModelField(field: DjangoField): ModelField {
  if (field.is_relation) {
    const relatedDbTable = getRelatedDbTable(field);
    const optionLabel = getOptionLabel(field);
    const isMany = field.many_to_many === true || field.one_to_many === true;

    return {
      key: field.key,
      label: field.label,
      type: "select",
      required: !field.blank,
      // IMPORTANT: must be db/<table> for your API routing (and for token logic)
      optionsEndpoint: `db/${relatedDbTable}`,
      optionLabel: optionLabel,
      ...(isMany && { isMultiple: true }),
    } as any;
  }

  if (field.type === "BooleanField") {
    return {
      key: field.key,
      label: field.label,
      type: "boolean",
      required: !field.blank,
    };
  }

  return {
    key: field.key,
    label: field.label,
    type: "text",
    required: !field.blank,
    ...(field.type === "TextField" ? { multiLine: true } : {}),
  };
}

// Enhanced RelatedCell component for single relations with rich tooltip
function RelatedCell({
                       value,
                       relatedRow,
                       onEdit,
                     }: {
  value: string;
  relatedRow: any;
  onEdit: () => void;
}) {
  const [hover, setHover] = useState(false);

  return (
    <span
      style={{ display: "inline-flex", alignItems: "center" }}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
    >
      <Tooltip
        title={
          relatedRow && typeof relatedRow === "object" ? (
            <Box>
              <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                {value}
              </Typography>
              <Typography variant="caption" component="div" sx={{ mt: 0.5 }}>
                {relatedRow.description && (
                  <div>
                    Description: {relatedRow.description.substring(0, 100)}
                    {relatedRow.description.length > 100 ? "..." : ""}
                  </div>
                )}
                {relatedRow.is_cisco !== undefined && <div>Cisco: {relatedRow.is_cisco ? "Yes" : "No"}</div>}
                {relatedRow.last_updated && <div>Updated: {new Date(relatedRow.last_updated).toLocaleDateString()}</div>}
                {relatedRow.email && <div>Email: {relatedRow.email}</div>}
                {relatedRow.part_number && <div>Part Number: {relatedRow.part_number}</div>}
                {relatedRow.deployment_type && <div>Type: {relatedRow.deployment_type}</div>}
                <div style={{ marginTop: "4px", fontSize: "0.7em", opacity: 0.8 }}>Click to edit</div>
              </Typography>
            </Box>
          ) : (
            value
          )
        }
        placement="top"
      >
        <span style={{ cursor: relatedRow ? "pointer" : "default" }}>
          <TruncatedText text={value} maxLength={100} showTooltip={false} />
        </span>
      </Tooltip>
      {hover && relatedRow && (
        <Tooltip title="Edit Related Item">
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              onEdit();
            }}
          >
            <EditIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
    </span>
  );
}

// Component for rendering one-to-many relationships with chips (enhanced for complex objects)
function OneToManyChips({
                          items,
                          field,
                          options,
                          onEditRelated,
                        }: {
  items: any[];
  field: DjangoField;
  options: any[];
  onEditRelated: (field: DjangoField, relatedRow: any) => void;
}) {
  const maxVisible = 4;
  const optionLabel = getOptionLabel(field);

  const itemsWithLabels = items.map((item, index) => {
    let relatedId: any, relatedObject: any, label: any;

    if (typeof item === "object" && item !== null) {
      relatedId = item.id;
      relatedObject = item;

      label =
        item[optionLabel] ||
        item.name ||
        item.title ||
        item.version ||
        item.part_number ||
        item.old_product_name ||
        item.email ||
        item.status ||
        `${field.key} #${item.id}`;
    } else {
      relatedId = item;
      relatedObject = options.find((o) => o.id === relatedId) || { id: relatedId };
      label =
        relatedObject[optionLabel] ||
        relatedObject.name ||
        relatedObject.title ||
        relatedObject.email ||
        `ID: ${relatedId}`;
    }

    return {
      item,
      rel: relatedObject,
      label: String(label),
      id: relatedId || `item-${index}`,
    };
  });

  const visibleItems = itemsWithLabels.slice(0, maxVisible);
  const hiddenItems = itemsWithLabels.slice(maxVisible);
  const hasMore = hiddenItems.length > 0;

  return (
    <Box display="flex" flexWrap="wrap" gap={0.5} alignItems="center">
      {visibleItems.map(({ rel, label, id }, index) => (
        <Tooltip
          key={id || index}
          title={
            <Box>
              <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                {label}
              </Typography>
              {rel && typeof rel === "object" && (
                <Typography variant="caption" component="div" sx={{ mt: 0.5 }}>
                  {rel.description && (
                    <div>
                      Description: {rel.description.substring(0, 100)}
                      {rel.description.length > 100 ? "..." : ""}
                    </div>
                  )}
                  {rel.version && <div>Version: {rel.version}</div>}
                  {rel.part_number && <div>Part Number: {rel.part_number}</div>}
                  {rel.released && <div>Released: {rel.released}</div>}
                  {rel.deployment_type && <div>Type: {rel.deployment_type}</div>}
                  {rel.old_product_name && <div>Old Name: {rel.old_product_name}</div>}
                  {rel.spec_summary && (
                    <div>
                      Specs: {rel.spec_summary.substring(0, 50)}
                      {rel.spec_summary.length > 50 ? "..." : ""}
                    </div>
                  )}
                  {rel.url && <div>URL: Available</div>}
                  {rel.last_update && <div>Last Update: {rel.last_update}</div>}
                  {rel.created && <div>Created: {new Date(rel.created).toLocaleDateString()}</div>}
                  <div style={{ marginTop: "4px", fontSize: "0.7em", opacity: 0.8 }}>Click to edit</div>
                </Typography>
              )}
            </Box>
          }
          placement="top"
        >
          <Chip
            label={<TruncatedText text={label} maxLength={15} showTooltip={false} />}
            size="small"
            variant="outlined"
            color="primary"
            onClick={rel ? () => onEditRelated(field, rel) : undefined}
            clickable={!!rel}
            sx={{
              maxWidth: "120px",
              cursor: rel ? "pointer" : "default",
              "&:hover": rel
                ? {
                  backgroundColor: "primary.light",
                  color: "primary.contrastText",
                }
                : {},
            }}
          />
        </Tooltip>
      ))}
      {hasMore && (
        <Tooltip
          title={
            <Box sx={{ maxHeight: "300px", overflow: "auto" }}>
              <Typography variant="body2" sx={{ fontWeight: "bold", mb: 1 }}>
                Additional {hiddenItems.length} items:
              </Typography>
              {hiddenItems.map(({ label, rel }, index) => (
                <Box key={index} sx={{ mb: 0.5 }}>
                  <Typography variant="caption" component="div">
                    • {label}
                  </Typography>
                  {rel && typeof rel === "object" && (
                    <Typography variant="caption" component="div" sx={{ ml: 1, opacity: 0.8 }}>
                      {rel.version && `v${rel.version} • `}
                      {rel.part_number && `${rel.part_number} • `}
                      {rel.released && `Released: ${rel.released}`}
                    </Typography>
                  )}
                </Box>
              ))}
            </Box>
          }
          placement="top"
        >
          <Chip
            label={`+${hiddenItems.length}`}
            size="small"
            variant="filled"
            color="secondary"
            sx={{
              cursor: "help",
              minWidth: "40px",
            }}
          />
        </Tooltip>
      )}
    </Box>
  );
}

// Multi-select field component with chip display
function MultiSelectField({
                            field,
                            value,
                            options,
                            onChange,
                            onEditRelated,
                            disabled = false,
                          }: {
  field: ModelField & { isMultiple?: boolean };
  value: any[];
  options: any[];
  onChange: (newValue: any[]) => void;
  onEditRelated?: (field: DjangoField, relatedRow: any) => void;
  disabled?: boolean;
}) {
  const selectedIds = useMemo(() => {
    if (!Array.isArray(value)) return [];
    return value
      .map((item) => {
        const itemId = typeof item === "object" ? item.id : item;
        return String(itemId);
      })
      .filter(Boolean);
  }, [value]);

  const handleChange = (event: SelectChangeEvent<typeof selectedIds>) => {
    const selectedValues = event.target.value as string[];
    const selectedItems = selectedValues.map((id) => {
      const existingItem = value.find((item) => {
        const itemId = typeof item === "object" ? item.id : item;
        return String(itemId) === String(id);
      });
      if (existingItem) return existingItem;

      const foundOption = options.find((option) => String(option.id) === String(id));
      return foundOption || { id: id };
    });
    onChange(selectedItems);
  };

  const handleDeleteChip = (itemToDelete: any) => {
    const itemId = typeof itemToDelete === "object" ? itemToDelete.id : itemToDelete;
    const newValue = value.filter((item) => {
      const currentId = typeof item === "object" ? item.id : item;
      return String(currentId) !== String(itemId);
    });
    onChange(newValue);
  };

  const getDisplayLabel = (item: any) => {
    const optionLabel = getFieldOptionLabel(field);

    if (typeof item === "object" && item !== null) {
      return (
        item[optionLabel] ||
        item.name ||
        item.title ||
        item.version ||
        item.part_number ||
        item.old_product_name ||
        item.email ||
        `ID: ${item.id}`
      );
    }
    const option = options.find((o) => String(o.id) === String(item));
    return option?.[optionLabel] || option?.name || option?.title || `ID: ${item}`;
  };

  return (
    <Box>
      <FormControl fullWidth margin="normal">
        <InputLabel>{field.label}</InputLabel>
        <Select
          multiple
          value={selectedIds}
          onChange={handleChange}
          input={<OutlinedInput label={field.label} />}
          disabled={disabled}
          renderValue={() => (
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
              {value.map((item, index) => {
                const label = getDisplayLabel(item);
                return (
                  <Chip
                    key={index}
                    label={<TruncatedText text={String(label)} maxLength={20} showTooltip={false} />}
                    size="small"
                    variant="outlined"
                    color="primary"
                    onDelete={disabled ? undefined : () => handleDeleteChip(item)}
                    onMouseDown={(e) => e.stopPropagation()}
                    onClick={
                      onEditRelated && typeof item === "object"
                        ? (e) => {
                          e.stopPropagation();
                          onEditRelated(field as any, item);
                        }
                        : undefined
                    }
                    clickable={!!(onEditRelated && typeof item === "object")}
                    sx={{
                      maxWidth: "150px",
                      "& .MuiChip-deleteIcon": {
                        fontSize: "16px",
                      },
                    }}
                  />
                );
              })}
            </Box>
          )}
          MenuProps={{
            PaperProps: {
              style: {
                maxHeight: 300,
                width: 250,
              },
            },
          }}
        >
          {options.map((option) => {
            const optionLabel = getFieldOptionLabel(field);
            const optionIdString = String(option.id);
            const isSelected = selectedIds.includes(optionIdString);

            return (
              <MenuItem key={option.id} value={optionIdString}>
                <Checkbox checked={isSelected} />
                <ListItemText
                  primary={option[optionLabel] || option.name || option.title || `ID: ${option.id}`}
                  secondary={option.description ? `${option.description.substring(0, 50)}...` : undefined}
                />
              </MenuItem>
            );
          })}
        </Select>
      </FormControl>

      {process.env.NODE_ENV === "development" && (
        <Typography variant="caption" color="textSecondary">
          Selected IDs: [{selectedIds.join(", ")}] | Values: {value.length} items
        </Typography>
      )}
    </Box>
  );
}

// Skeleton table component
function SkeletonTable({ columns }: { columns: any[] }) {
  return (
    <TableContainer component={Paper}>
      <Table>
        <TableHead>
          <TableRow>
            {columns.map((column, index) => (
              <TableCell key={index}>
                <Skeleton variant="text" width={120} />
              </TableCell>
            ))}
            <TableCell width={120}>
              <Skeleton variant="text" width={80} />
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {Array.from({ length: 5 }).map((_, rowIndex) => (
            <TableRow key={rowIndex}>
              {columns.map((_, colIndex) => (
                <TableCell key={colIndex}>
                  <Skeleton variant="text" width={Math.random() * 100 + 80} />
                </TableCell>
              ))}
              <TableCell>
                <Box display="flex" gap={1}>
                  <Skeleton variant="circular" width={24} height={24} />
                  <Skeleton variant="circular" width={24} height={24} />
                </Box>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

/**
 * UPDATED:
 * Use shared fetchItems() so db/* requests include tb_jwt Authorization header
 * (and your hardened queries.ts token selection logic is respected).
 */
const fetchDjangoItems = async (endpoint: string, params: Record<string, any> = {}) => {
  return fetchItems(endpoint, params);
};

// --- MAIN COMPONENT ---
export function DjangoModelAdmin() {
  const [selectedDbTable, setSelectedDbTable] = useState<string>("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editItem, setEditItem] = useState<any>(null);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<any>(null);
  const [relatedDialogOpen, setRelatedDialogOpen] = useState(false);
  const [relatedDialogConfig, setRelatedDialogConfig] = useState<RelatedDialogConfig | null>(null);

  // Pagination and filtering state
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [filters, setFilters] = useState<Record<string, any>>({});

  const queryClient = useQueryClient();

  // Fetch all models
  const { data: apiData, isLoading: modelsLoading, error: modelsError } = useQuery<{ models: DjangoModel[] }>({
    queryKey: ["models"],
    queryFn: () => fetchDjangoItems("db/tables"),
  });

  const models: DjangoModel[] = useMemo(() => apiData?.models ?? [], [apiData]);

  // Auto-select first table when models are loaded
  useEffect(() => {
    if (models.length > 0 && !selectedDbTable) {
      const sortedModels = [...models].sort((a, b) => a.label.localeCompare(b.label));
      const firstTable = sortedModels[0].db_table;
      setSelectedDbTable(firstTable);
    }
  }, [models, selectedDbTable]);

  const selectedModel = useMemo(() => models.find((m) => m.db_table === selectedDbTable) ?? null, [models, selectedDbTable]);

  // Reset pagination when table changes
  useEffect(() => {
    setPage(0);
    setFilters({});
  }, [selectedDbTable]);

  // Related options cache
  const [relatedOptions, setRelatedOptions] = useState<{ [key: string]: any[] }>({});
  const [relatedOptionsLoading, setRelatedOptionsLoading] = useState(false);

  useEffect(() => {
    let active = true;
    if (!selectedModel) {
      setRelatedOptions({});
      return;
    }

    const relFields = selectedModel.fields.filter((f) => !!f.is_relation);
    if (!relFields.length) {
      setRelatedOptions({});
      return;
    }

    setRelatedOptionsLoading(true);

    const promises = relFields.map(async (f) => {
      try {
        const dbTable = getRelatedDbTable(f);
        // For related options, fetch a large number without pagination
        const data = await fetchDjangoItems(`db/${dbTable}`, { size: 1000, page: 1 });
        const items = (data as any)?.items || data || [];
        return [f.key, items];
      } catch (error) {
        console.error(`Failed to fetch options for field ${f.key}:`, error);
        return [f.key, []];
      }
    });

    Promise.all(promises)
      .then((results) => {
        if (!active) return;
        const merged: { [key: string]: any[] } = {};
        results.forEach(([key, items]: [string, any[]]) => {
          merged[key] = items;
        });
        setRelatedOptions(merged);
        setRelatedOptionsLoading(false);
      })
      .catch((error) => {
        console.error("Error loading related options:", error);
        if (active) {
          setRelatedOptionsLoading(false);
        }
      });

    return () => {
      active = false;
    };
  }, [selectedModel]);

  // Build query parameters for Django API
  const queryParams = useMemo(() => {
    const params: Record<string, any> = {
      page: page + 1, // Django expects 1-based page numbers
      size: pageSize,
      detailed: true,
    };

    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== "") {
        params[key] = value;
      }
    });

    return params;
  }, [page, pageSize, filters]);

  // Table data fetch with pagination and filtering
  const {
    data: apiResponse,
    isLoading: rowsLoading,
    error: rowsError,
    isFetching: rowsFetching,
  } = useQuery({
    queryKey: ["table-rows", selectedDbTable, page, pageSize, filters],
    queryFn: () => {
      if (!selectedDbTable) return { items: [], total_count: 0 };
      return fetchDjangoItems(`db/${selectedDbTable}`, queryParams);
    },
    ...{
        enabled: !!selectedDbTable,
        placeholderData: keepPreviousData,
      },
  });

  // Extract rows and pagination info from API response
  const { rows, totalCount } = useMemo(() => {
    if (!apiResponse) {
      return { rows: [], totalCount: 0 };
    }

    // Handle Django's paginated response format
    if ((apiResponse as any).items && Array.isArray((apiResponse as any).items)) {
      const total = (apiResponse as any).total_count || 0;
      return { rows: (apiResponse as any).items, totalCount: total };
    }

    // Handle direct array responses
    if (Array.isArray(apiResponse)) {
      return { rows: apiResponse, totalCount: apiResponse.length };
    }

    return { rows: [], totalCount: 0 };
  }, [apiResponse]);

  // Determine overall loading state
  const isTableLoading = rowsLoading || relatedOptionsLoading || rowsFetching;

  // Mutations
  const upsertMutation = useMutation<any, unknown, any>({
    mutationFn: async (data) => {
      return data.id
        ? putItem(`db/${selectedDbTable}`, data)
        : postItem(`db/${selectedDbTable}`, data);
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['table-rows', selectedDbTable] });
      // A user's territory (sales_level_*) drives their Salesforce opportunity/
      // account list scope, but those lists are cached by email in the salesforce
      // service and stay stale until TTL. When a territory-bearing user record is
      // saved, proactively clear that user's scoped caches so the new territory
      // takes effect immediately instead of on the next cache expiry.
      const saved = variables as Record<string, any> | undefined;
      const savedEmail = saved?.email;
      const hasTerritory = !!saved && Object.keys(saved).some((k) => /^sales_level_\d$/.test(k));
      if (savedEmail && hasTerritory) {
        postItem('sf/cache/invalidate-user-scope', { email: savedEmail }).catch((err) => {
          console.error('Failed to invalidate Salesforce scope cache for user:', err);
        });
      }
      setDialogOpen(false);
      setEditItem(null);
    },
    onError: (error) => {
      console.error('Upsert error:', error);
    },
  });

  const deleteMutation = useMutation<any, unknown, any>({
    mutationFn: async (data) => {
      if (!data.id) {
        alert('Unable to Delete, item must have ID value');
        setDialogOpen(false);
        setEditItem(null);
        return;
      }
      return deleteItem(`db/${selectedDbTable}/${data.id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['table-rows', selectedDbTable] });
      setDeleteTarget(null);
    },
    onError: (error) => {
      console.error('Delete error:', error);
    },
  });

  // Related upsert
  const relatedUpsertMutation = useMutation<any, unknown, { relatedDbTable: string; data: any }>({
    mutationFn: async ({ relatedDbTable, data }) => {
      return data.id ? putItem(`db/${relatedDbTable}`, data) : postItem(`db/${relatedDbTable}`, data);
    },
    onSuccess: (_, { relatedDbTable }) => {
      Object.keys(relatedOptions).forEach(() => {
        queryClient.invalidateQueries({ queryKey: ['models'] });
        queryClient.invalidateQueries({ queryKey: ['table-rows', relatedDbTable] });
      });
      queryClient.invalidateQueries({ queryKey: ['table-rows', selectedDbTable] });
      setRelatedDialogOpen(false);
      setRelatedDialogConfig(null);
    },
    onError: (error) => {
      console.error('Related upsert error:', error);
    },
  });

  // Related modal open handler
  const openRelatedModal = useCallback(
    (field: DjangoField, relatedRow: any) => {
      if (!relatedRow || !field) return;

      const relatedDbTable = getRelatedDbTable(field);
      const relatedModel = models.find((m) => m.db_table === relatedDbTable);

      if (!relatedModel) {
        console.error("Could not find related model for field:", field.key, "looking for table:", relatedDbTable);
        return;
      }

      setRelatedDialogConfig({
        model: relatedModel,
        fields: relatedModel.fields,
        data: relatedRow,
        db_table: relatedModel.db_table,
      });
      setRelatedDialogOpen(true);
    },
    [models],
  );

  // Table config with custom render for FKs
  const modelConfig: ModelConfig | undefined = useMemo(() => {
    if (!selectedModel) return undefined;

    const columns = selectedModel.fields.map((f) => {
      if (f.is_relation) {
        const isMany = f.many_to_many === true || f.one_to_many === true;
        return {
          key: f.key,
          label: f.label || f.key,
          render: (row: any) => {
            const value = row[f.key];
            const options = relatedOptions[f.key] || [];

            if (!value) return "";

            if (isMany && Array.isArray(value)) {
              return <OneToManyChips items={value} field={f} options={options} onEditRelated={openRelatedModal} />;
            }

            let relatedId = value;
            let relatedObject = value;

            if (typeof value === "object" && value !== null) {
              relatedId = value.id;
              relatedObject = value;
            } else {
              relatedObject = options.find((o) => o.id === relatedId) || { id: relatedId };
            }

            const optionLabel = getOptionLabel(f);
            const label =
              relatedObject?.[optionLabel] ||
              relatedObject?.name ||
              relatedObject?.title ||
              relatedObject?.email ||
              relatedObject?.id ||
              relatedId;

            return <RelatedCell value={String(label)} relatedRow={relatedObject} onEdit={() => openRelatedModal(f, relatedObject)} />;
          },
        };
      }

      if (shouldRenderAsComplex(f)) {
        return {
          key: f.key,
          label: f.label || f.key,
          render: (row: any) => renderComplexValue(row[f.key]),
        };
      }

      return {
        key: f.key,
        label: f.label || f.key,
        render: (row: any) => <TruncatedText text={String(row[f.key] || "")} maxLength={100} />,
      };
    });

    return {
      key: selectedModel.db_table,
      label: selectedModel.label,
      endpoint: `db/${selectedModel.db_table}`,
      columns,
      fields: selectedModel.fields.map(djangoFieldToModelField),
    };
  }, [selectedModel, relatedOptions, openRelatedModal]);

  // Dialog fields
  const dialogFields: ModelField[] = useMemo(() => (selectedModel ? selectedModel.fields.map(djangoFieldToModelField) : []), [selectedModel]);

  const relatedDialogFields: ModelField[] = useMemo(
    () => (relatedDialogConfig?.model ? relatedDialogConfig.model.fields.map(djangoFieldToModelField) : []),
    [relatedDialogConfig],
  );

  // Pagination handlers
  const handleChangePage = useCallback((event: unknown, newPage: number) => {
    setPage(newPage);
  }, []);

  const handleChangeRowsPerPage = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const newPageSize = parseInt(event.target.value, 10);
    setPageSize(newPageSize);
    setPage(0);
  }, []);

  // Filter handlers
  const handleFilterChange = useCallback((filterName: string, value: any) => {
    setFilters((prev) => ({
      ...prev,
      [filterName]: value,
    }));
    setPage(0);
  }, []);

  const clearFilters = useCallback(() => {
    setFilters({});
    setPage(0);
  }, []);

  // CRUD Handlers
  const handleSelect = (event: SelectChangeEvent<string>) => {
    const newTable = event.target.value as string;
    setSelectedDbTable(newTable);
  };

  const handleAdd = useCallback(() => {
    setEditItem(null);
    setDialogOpen(true);
  }, []);

  const handleEdit = useCallback((row: any) => {
    setEditItem(row);
    setDialogOpen(true);
  }, []);

  const handleDelete = useCallback((row: any) => {
    setDeleteTarget(row);
    setConfirmModalOpen(true);
  }, []);

  const handleSave = useCallback(() => {
    upsertMutation.mutate(editItem);
  }, [editItem, upsertMutation]);

  const handleConfirmDelete = async (): Promise<void> => {
    if (deleteTarget) {
      await deleteMutation.mutateAsync(deleteTarget);
    } else {
      throw new Error("No delete target specified.");
    }
  };

  // AdminDialog selectOptions mapping
  const selectOptions = useMemo(() => {
    if (!selectedModel) return {};
    const map: { [key: string]: any[] } = {};
    selectedModel.fields.forEach((f) => {
      if (f.is_relation) {
        map[f.key] = relatedOptions[f.key] || [];
      }
    });
    return map;
  }, [selectedModel, relatedOptions]);

  // Related AdminDialog selectOptions mapping
  const relatedDialogSelectOptions = useMemo(() => {
    if (!relatedDialogConfig) return {};
    const relatedModel = relatedDialogConfig.model;
    const map: { [key: string]: any[] } = {};
    relatedModel.fields.forEach((f) => {
      if (f.is_relation) {
        map[f.key] = relatedOptions[f.key] || [];
      }
    });
    return map;
  }, [relatedDialogConfig, relatedOptions]);

  // Error handling
  if (modelsError) {
    return (
      <Box>
        <Typography color="error">
          Failed to load models: {modelsError instanceof Error ? modelsError.message : "Unknown error"}
        </Typography>
      </Box>
    );
  }

  // Show loading state while models are loading
  if (modelsLoading) {
    return (
      <Box>
        <Typography variant="h5" mb={3}>
          Database Table Admin
        </Typography>
        <StbLoader isLoading={true} />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={3}>
        <Typography variant="h5">Database Table Admin</Typography>
        <FormControl sx={{ minWidth: 300 }}>
          <InputLabel>Choose Model</InputLabel>
          <Select value={selectedDbTable} label="Choose Model" onChange={handleSelect} disabled={!models || models.length === 0}>
            {[...models]
              .sort((a, b) => a.label.localeCompare(b.label))
              .map((model) => (
                <MenuItem key={model.db_table} value={model.db_table}>
                  {model.label}
                </MenuItem>
              ))}
          </Select>
        </FormControl>
      </Box>

      {/* Error display */}
      {rowsError && (
        <Box mb={2}>
          <Typography color="error">
            Failed to load table data: {rowsError instanceof Error ? rowsError.message : "Unknown error"}
          </Typography>
        </Box>
      )}

      {/* Filters Section */}
      {selectedDbTable && selectedModel && Object.keys(filters).length > 0 && (
        <Box mb={2} p={2} bgcolor="grey.50" borderRadius={1}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
            <Typography variant="h6">Active Filters</Typography>
            <IconButton onClick={clearFilters} size="small">
              Clear All
            </IconButton>
          </Box>
          <Box display="flex" flexWrap="wrap" gap={1}>
            {Object.entries(filters).map(([key, value]) => (
              <Chip key={key} label={`${key}: ${value}`} onDelete={() => handleFilterChange(key, undefined)} size="small" />
            ))}
          </Box>
        </Box>
      )}

      {/* Table or Loading State */}
      <Box position="relative">
        <StbLoader isLoading={isTableLoading} />

        {selectedDbTable && !modelConfig && !isTableLoading && (
          <Typography color="error">
            No table definition found for <strong>{selectedDbTable}</strong>
          </Typography>
        )}

        {selectedDbTable && modelConfig && (
          <>
            {rowsLoading && !rowsFetching ? (
              <SkeletonTable columns={modelConfig.columns} />
            ) : rows.length === 0 && !isTableLoading ? (
              <>
                <Box mb={2} display="flex" justifyContent="space-between" alignItems="center">
                  <Typography variant="h6" color="textSecondary">
                    No data found for {selectedModel?.label}
                  </Typography>
                  <IconButton onClick={handleAdd} color="primary">
                    Add New Item
                  </IconButton>
                </Box>
                <SkeletonTable columns={modelConfig.columns} />
              </>
            ) : !isTableLoading ? (
              <AdminTable model={modelConfig} rows={rows} loading={false} onAdd={handleAdd} onEdit={handleEdit} onDelete={handleDelete} />
            ) : null}
          </>
        )}
      </Box>

      {/* Pagination Control - Below Table */}
      {selectedDbTable && !rowsLoading && (
        <Box mt={2}>
          <Box position="relative">
            <StbLoader isLoading={rowsFetching && !rowsLoading} />

            <TablePagination
              component="div"
              count={totalCount}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={pageSize}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[10, 25, 50, 100, 250]}
              showFirstButton
              showLastButton
              labelRowsPerPage="Items per page:"
              labelDisplayedRows={({ from, to, count }) => `${from}-${to} of ${count !== -1 ? count : `more than ${to}`}`}
              disabled={rowsFetching}
            />
          </Box>

          <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
            Page {page + 1} • Size {pageSize} • Total {totalCount} • Showing {rows.length} items
            {Object.keys(filters).length > 0 && ` • ${Object.keys(filters).length} filter(s) active`}
            {rowsFetching && " • Loading..."}
          </Typography>
        </Box>
      )}

      {/* Main Admin Dialog */}
      {modelConfig && (
        <AdminDialog
          open={dialogOpen}
          model={modelConfig}
          fields={dialogFields}
          data={editItem || {}}
          setData={setEditItem}
          onSave={handleSave}
          onCancel={() => {
            setDialogOpen(false);
            setEditItem(null);
          }}
          isSaving={upsertMutation.isPending}
          selectOptions={selectOptions}
          isLoading={rowsLoading || relatedOptionsLoading}
          multiSelectRenderer={(field, value, onChange) => (
            <MultiSelectField
              field={field}
              value={value || []}
              options={(selectOptions as any)[field.key] || []}
              onChange={onChange}
              onEditRelated={openRelatedModal}
              disabled={upsertMutation.isPending}
            />
          )}
        />
      )}

      {/* Confirm Modal for Deletion */}
      <StbApiModal
        title="Confirm Deletion"
        open={confirmModalOpen}
        role="delete"
        onClose={async () => setConfirmModalOpen(false)}
        onSubmit={handleConfirmDelete}
        submitSuccess="The operation completed successfully"
        submitError="The operation could not be completed. Please try again."
      >
        Are you sure you want to delete this item? This action cannot be undone.
      </StbApiModal>

      {/* Related Item Edit Modal */}
      {relatedDialogConfig && (
        <AdminDialog
          open={relatedDialogOpen}
          model={{
            key: relatedDialogConfig.model.db_table,
            label: relatedDialogConfig.model.label,
            endpoint: `db/${relatedDialogConfig.model.db_table}`,
            columns: relatedDialogConfig.model.fields.map((f) => ({ key: f.key, label: f.label || f.key })),
            fields: relatedDialogFields,
          }}
          fields={relatedDialogFields}
          data={relatedDialogConfig.data}
          setData={(d) => setRelatedDialogConfig((cfg) => (cfg ? { ...cfg, data: d } : cfg))}
          onSave={() => {
            relatedUpsertMutation.mutate({
              relatedDbTable: relatedDialogConfig.db_table,
              data: relatedDialogConfig.data,
            });
          }}
          onCancel={() => {
            setRelatedDialogOpen(false);
            setRelatedDialogConfig(null);
          }}
          isSaving={relatedUpsertMutation.isPending}
          selectOptions={relatedDialogSelectOptions}
          isLoading={relatedUpsertMutation.isPending}
          multiSelectRenderer={(field, value, onChange) => (
            <MultiSelectField
              field={field}
              value={value || []}
              options={(relatedDialogSelectOptions as any)[field.key] || []}
              onChange={onChange}
              onEditRelated={openRelatedModal}
              disabled={relatedUpsertMutation.isPending}
            />
          )}
        />
      )}
    </Box>
  );
}